import { useState, useEffect } from 'react';
import useClipboard from './useClipboard';

/**
 * Custom hook for working with Kaminari share URLs
 * 
 * @param {Object} options - Hook options
 * @param {string} options.defaultUrl - Default URL to use if none is provided
 * @param {string} options.paramName - URL parameter name to extract (default: 'shareUrl')
 * @returns {Object} - Hook methods and state
 */
const useShareUrlExtractor = ({
  defaultUrl = window.location.href,
  paramName = 'shareUrl'
} = {}) => {
  const [url, setUrl] = useState(defaultUrl);
  const [extractedUrl, setExtractedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [lastCopiedUrl, setLastCopiedUrl] = useState(null);
  const { extractUrlParam, copyToClipboard, copyStatus } = useClipboard();

  // Attempt to extract the share URL when the URL changes
  useEffect(() => {
    try {
      const extracted = extractUrlParam(url, paramName);
      setExtractedUrl(extracted);
      setError(null);
    } catch (err) {
      console.error('Error extracting share URL:', err);
      setExtractedUrl(null);
      setError(`Failed to extract ${paramName}: ${err.message}`);
    }
  }, [url, paramName, extractUrlParam]);

  /**
   * Update the URL to extract from
   * @param {string} newUrl - The new URL to extract from
   */
  const updateUrl = (newUrl) => {
    if (newUrl && typeof newUrl === 'string') {
      setUrl(newUrl);
    }
  };

  /**
   * Copies the extracted URL to clipboard
   * @returns {Promise<boolean>} - Whether the copy was successful
   */
  const copyExtractedUrl = async () => {
    if (!extractedUrl) {
      setError(`No ${paramName} parameter found in the URL`);
      return false;
    }

    try {
      await copyToClipboard(extractedUrl);
      setLastCopiedUrl(extractedUrl);
      return true;
    } catch (err) {
      setError(`Failed to copy ${paramName}: ${err.message}`);
      return false;
    }
  };

  /**
   * Copy the full URL to clipboard
   * @returns {Promise<boolean>} - Whether the copy was successful
   */
  const copyFullUrl = async () => {
    try {
      await copyToClipboard(url);
      setLastCopiedUrl(url);
      return true;
    } catch (err) {
      setError(`Failed to copy URL: ${err.message}`);
      return false;
    }
  };

  /**
   * Gets the last URL segment for display purposes
   * @param {string} urlString - URL to process
   * @returns {string} - The last segment of the URL
   */
  const getUrlDisplayName = (urlString) => {
    try {
      if (!urlString) return '';
      
      // Remove trailing slashes
      urlString = urlString.replace(/\/+$/, '');
      
      // Try to extract the last path segment
      const segments = urlString.split('/');
      let lastSegment = segments[segments.length - 1];
      
      // If it includes a query string or hash, extract just the path part
      if (lastSegment.includes('?') || lastSegment.includes('#')) {
        lastSegment = lastSegment.split(/[?#]/)[0];
      }
      
      return lastSegment || urlString;
    } catch (e) {
      return urlString;
    }
  };

  return {
    url,
    updateUrl,
    extractedUrl,
    error,
    copyExtractedUrl,
    copyFullUrl,
    copyStatus,
    lastCopiedUrl,
    getUrlDisplayName
  };
};

export default useShareUrlExtractor; 